import * as _ from "lodash";
import * as React from "react";
import * as S from "@styled";
import { connect } from "react-redux";
import { DispatchType, Utilities } from "../../shared/utilities";
import { BatchUpdateElements, batchUpdateElements } from "../actions/AddOrUpdateElement";
import { editorReady } from "../actions/EditorReadyAction";
import { clearElementState } from "../actions/ImportExportActions";
import { CopiedElement, pasteElements } from "../actions/PasteElements";
import { openProcessSidePanel } from "../actions/SidePanelActions";
import { updateBpmn } from "../actions/UpdateBpmn";
import { updateVariableScopes } from "../actions/VariableScopeAnalyzer";
import { IElement, IElementDictionary, ITaskData } from "../model/IProcessData";
import { Process } from "../model/Process";
import { ProcessSidePanelType, State } from "../model/State";
import { ValidationErrorDictionary, ValidationErrorType } from "../model/ValidationData";
import { BpmnEditor } from "./BpmnEditor";
import { hasValidationError } from "./Helpers";
import useUserTheme from "shared/styles/hooks/useUserTheme";

export const EditorPanel: React.FC<EditorPanelProps> = (props: EditorPanelProps) => {
    const [theme] = useUserTheme();
    let bpmnEditor;
    if (!props.process.bpmn) {
        // there must have been an error loading the BPMN, most likely due to migration failing
        bpmnEditor = (
            <S.ErrorContainer>
                <div className="alert-warning">
                    <div className="alert-label">
                        {FRENDS.Localizations.ProcessBpmnNotFound} {props.process.isMigration
                            ? FRENDS.Localizations.ProcessMigrationFailed
                            : null}
                    </div>
                </div>
            </S.ErrorContainer>
        );
    } else {
        bpmnEditor = <BpmnEditor
            theme={theme}
            disableEdit={props.disableEdit}
            bpmn={props.process.bpmn}
            elementParameters={props.elementNodes}
            tasks={props.tasks}
            onChange={props.updateBpmn}
            onElementSelect={props.handleSelectElement}
            onLoaded={props.processLoaded}
            onElementsPasted={props.elementsPasted}
            isSubprocess={props.isSubprocess}
            validationErrorDictionary={props.validation}
            onElementsBatchUpdate={props.elementsBatchUpdate}
            onErrorMessage={props.onErrorMessage}
            onImport={props.onImport}
        />;
    }

    return (
        <>
            <S.ProcessEditorHeader className="process-editor-header-selector">
                <S.HeaderNameAndContentContainer onClick={props.selectSettingsPanel} style={{ width: 'fit-content' }}>
                    <S.FlexContainer asColumn>
                        <S.FlexItem>
                            <h3>Process editor /</h3>
                        </S.FlexItem>
                        <S.FlexItem style={{ display: "flex", alignItems: "baseline" }}>
                            <S.ProcessName id="processNameTitle">
                                {props.process.name ? props.process.name : <S.PlaceholderText hasError={hasValidationError((props.validation as ValidationErrorDictionary)["process"], ValidationErrorType.NoProcessName)}>{FRENDS.Localizations.Name}</S.PlaceholderText>}
                            </S.ProcessName>
                            <S.ProcessVersion>{props.process.version ? `v.${props.process.majorVersion}.${props.process.minorVersion}.${props.process.version}` : null}</S.ProcessVersion>
                            {props.process.isDraft ? <span>&nbsp;({FRENDS.Localizations.Draft})</span> : null}
                            {props.process.isSubprocess ? <span>&nbsp;({FRENDS.Localizations.Subprocess})</span> : null}
                            {props.process.hasGlobalErrorHandler ?
                                <span onClick={props.handleGlobalErrorHandleSelect} className="pointer" title={FRENDS.Localizations.Show + " " + FRENDS.Localizations.ProcessErrorHandler.toLowerCase()}>
                                    &nbsp;
                                    <S.PlaceholderText hasError={_.has(props.validation, "globalErrorHandler")}>
                                        ({FRENDS.Localizations.Has} {FRENDS.Localizations.ProcessErrorHandler.toLowerCase()})
                                    </S.PlaceholderText>
                                </span> : null}
                            {props.isEditorReadOnly ? (
                                <span>&nbsp;(read-only)</span>
                            ) : null}
                            {props.process.isMigration ? <span>
                                {FRENDS.Localizations.ProcessConverted}
                            </span> : null}
                        </S.FlexItem>
                    </S.FlexContainer>
                </S.HeaderNameAndContentContainer>
            </S.ProcessEditorHeader>
            {bpmnEditor}
        </>
    );
};

interface EditorPanelOwnProps {
    disableEdit?: boolean | undefined;
    validation?: ValidationErrorDictionary | undefined;
    handleSelectElement: (element: IElement) => void;
    handleGlobalErrorHandleSelect: () => void;
    selectSettingsPanel?: (() => void) | undefined;
}

interface EditorPanelStateProps {
    tasks: ITaskData[];
    process: Process;
    elementNodes: IElementDictionary;
    isSubprocess: boolean | undefined;
    isEditorReadOnly?: boolean;
}

interface EditorPanelDispatchProps {
    onImport: (batch: BatchUpdateElements) => void;
    onErrorMessage: (error: string) => void;
    processLoaded: () => void;
    elementsPasted: (copiesElements: CopiedElement[]) => void;
    elementsBatchUpdate: (batch: BatchUpdateElements) => void;
    updateBpmn: (updatedBpmn: string) => void;
}

export type EditorPanelProps = EditorPanelOwnProps & EditorPanelStateProps & EditorPanelDispatchProps;
const mapStateToProps = (state: State, ownProps: EditorPanelProps): EditorPanelStateProps => {
    return {
        tasks: state.tasks,
        elementNodes: state.elements,
        isSubprocess: state.process.isSubprocess,
        process: state.process,
        isEditorReadOnly: state.uiState.isEditorReadOnly
    };
};

const mapDispatchToProps = (dispatch: DispatchType<State>, ownProps: EditorPanelOwnProps): EditorPanelDispatchProps => {
    return {
        updateBpmn: (updatedBpmn) => dispatch(updateBpmn(updatedBpmn)),
        processLoaded: () => {
            if (!ownProps.disableEdit) { dispatch(updateVariableScopes()); }
            dispatch(editorReady()), dispatch(openProcessSidePanel(ProcessSidePanelType.SettingsPanel));
        },
        elementsPasted: (pastedElements: CopiedElement[]) => dispatch(pasteElements(pastedElements)),
        elementsBatchUpdate: (batch: BatchUpdateElements) => dispatch(batchUpdateElements(batch)),
        onErrorMessage: (error: string) => Utilities.toastError(error),
        onImport: (batch: BatchUpdateElements) => { dispatch(clearElementState()), dispatch(batchUpdateElements(batch)); }
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(EditorPanel);
